import React, { useState, useEffect, useRef } from 'react';

const AuthModal = ({ isOpen, onClose, formType = 'login' }) => {
  const [currentForm, setCurrentForm] = useState(formType);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    state: '',
    phone1: '',
    phone2: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const modalRef = useRef(null);
  const formsWrapperRef = useRef(null);

  // Updated API Base URL - point to your Go backend
  const API_BASE_URL = 'http://localhost:8081/api';

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return { isValid: true, message: '' };
    if (phone.length !== 10) {
      return { isValid: false, message: 'Phone number must be exactly 10 digits.' };
    }
    if (!/^(05|06|07)\d{8}$/.test(phone)) {
      return { isValid: false, message: 'Phone number must start with 05, 06, or 07.' };
    }
    return { isValid: true, message: 'Valid phone number.' };
  };

  const validateEmail = (email) => {
    if (!email) return { isValid: true, message: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Invalid email format.' };
    }
    return { isValid: true, message: 'Valid email address.' };
  };

  const validatePassword = (password) => {
    if (!password) return { isValid: false, message: '' };
    
    const requirements = [];
    if (password.length < 8) requirements.push('At least 8 characters long.');
    if (!/[A-Z]/.test(password)) requirements.push('At least one uppercase letter.');
    if (!/[a-z]/.test(password)) requirements.push('At least one lowercase letter.');
    if (!/[0-9]/.test(password)) requirements.push('At least one number.');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      requirements.push('At least one special character (e.g., #, *, @).');
    }

    if (requirements.length === 0) {
      return { isValid: true, message: 'Password strength: Strong.' };
    } else {
      return { 
        isValid: false, 
        message: `Password requirements: ${requirements.join(' ')}` 
      };
    }
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return { isValid: false, message: '' };
    if (password === confirmPassword) {
      return { isValid: true, message: 'Passwords match.' };
    } else {
      return { isValid: false, message: 'Passwords do not match.' };
    }
  };

  // Get validation results
  const getValidationResults = () => {
    return {
      phone1Validation: validatePhone(signupData.phone1),
      phone2Validation: validatePhone(signupData.phone2),
      emailValidation: validateEmail(signupData.email),
      passwordValidation: validatePassword(signupData.password),
      confirmPasswordValidation: validateConfirmPassword(signupData.password, signupData.confirmPassword)
    };
  };

  useEffect(() => {
    setCurrentForm(formType);
  }, [formType]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (formsWrapperRef.current) {
        formsWrapperRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const switchForm = (form) => {
    setCurrentForm(form);
    setErrorMessage('');
    if (formsWrapperRef.current) {
      formsWrapperRef.current.scrollTop = 0;
    }
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignupChange = (e) => {
    setSignupData({
      ...signupData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      console.log('🔵 Attempting login...', loginData);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      console.log('🔵 Response status:', response.status);
      
      const result = await response.json();
      console.log('🔵 Response data:', result);
      
      if (response.ok && result.success) {
        // Store token and user data in localStorage
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('userRole', result.role);
        localStorage.setItem('userId', result.userId);
        localStorage.setItem('userData', JSON.stringify(result.user));
        
        console.log('✅ Login successful');
        
        // Redirect based on role
        if (result.role === 'admin') {
          window.location.href = '/admin-profile';
        } else if (result.role === 'teacher') {
          window.location.href = '/teacher-profile';
        } else {
          window.location.href = '/student-profile';
        }
        
        onClose();
      } else {
        setErrorMessage(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setErrorMessage('Failed to connect to server. Please make sure the backend is running on port 8081.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    // Client-side validation
    if (signupData.password !== signupData.confirmPassword) {
      setErrorMessage("Passwords don't match");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        // Store token and user data in localStorage
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('userRole', result.role);
        localStorage.setItem('userId', result.userId);
        localStorage.setItem('userData', JSON.stringify(result.user));
        
        console.log('✅ Signup successful:', result);
        
        // Redirect based on role (default to student)
        if (result.role === 'teacher') {
          window.location.href = '/teacher-profile';
        } else {
          window.location.href = '/student-profile';
        }
        onClose();
      } else {
        setErrorMessage(result.message || 'Signup failed');
      }
    } catch (error) {
      console.error('❌ Signup error:', error);
      setErrorMessage('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get validation results
  const {
    phone1Validation,
    phone2Validation,
    emailValidation,
    passwordValidation,
    confirmPasswordValidation
  } = getValidationResults();

  return (
    <div className={`auth-modal-overlay ${isOpen ? 'active' : ''}`}>
      <div className="auth-modal" id="authModal" ref={modalRef}>
        <button 
          className="auth-modal-close" 
          onClick={onClose}
          aria-label="Close login/signup modal"
          disabled={isLoading}
        >
          <i className="fas fa-times"></i>
        </button>
        
        <div className="auth-modal-header">
          <h2 id="authModalTitle">
            {currentForm === 'login' ? 'Login to AMALIKA WORLD' : 'Join AMALIKA WORLD'}
          </h2>
          <p id="authModalSubtitle">
            {currentForm === 'login' 
              ? 'Access your personalized learning portal.' 
              : 'Create your account and start learning!'
            }
          </p>
        </div>
        
        {/* Error Message Display */}
        {errorMessage && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            {errorMessage}
          </div>
        )}
        
        <div className="auth-form-container">
          <div 
            className="auth-forms-wrapper" 
            style={{ transform: currentForm === 'login' ? 'translateX(0%)' : 'translateX(-50%)' }}
          >
            {/* Login Form */}
            <form className="auth-form login-form" onSubmit={handleLoginSubmit}>
              <div className="form-group">
                <label htmlFor="loginEmail">Email</label>
                <input
                  type="email"
                  id="loginEmail"
                  name="email"
                  placeholder="mohammed.chaib@gmail.com"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="loginPassword">Password</label>
                <input
                  type={showLoginPassword ? 'text' : 'password'}
                  id="loginPassword"
                  name="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  disabled={isLoading}
                />
                <i 
                  className={`fas ${showLoginPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon`}
                  onClick={() => setShowLoginPassword(!showLoginPassword)}
                  aria-label="Toggle password visibility"
                ></i>
              </div>
              
              <div className="form-actions">
                <button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </div>
              
              <p 
                className="toggle-form-link" 
                tabIndex="0" 
                role="button"
                onClick={() => switchForm('signup')}
                onKeyPress={(e) => e.key === 'Enter' && switchForm('signup')}
                aria-label="Switch to signup"
              >
                Don't have an account? <strong>Sign Up</strong>
              </p>
            </form>

            {/* Signup Form */}
            <form className="auth-form signup-form" onSubmit={handleSignupSubmit}>
              <div className="form-scroll-container" ref={formsWrapperRef}>
                <div className="form-group">
                  <label htmlFor="signupFirstName">First Name</label>
                  <input
                    type="text"
                    id="signupFirstName"
                    name="firstName"
                    placeholder="John"
                    value={signupData.firstName}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupLastName">Last Name</label>
                  <input
                    type="text"
                    id="signupLastName"
                    name="lastName"
                    placeholder="Doe"
                    value={signupData.lastName}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="dob">Date of Birth</label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    value={signupData.dob}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="state">State of Birth</label>
                  <select
                    id="state"
                    name="state"
                    value={signupData.state}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  >
                    <option value="" disabled>Select your state</option>
                    <option value="Algiers">Algiers</option>
                    <option value="Oran">Oran</option>
                    <option value="Constantine">Constantine</option>
                    <option value="Annaba">Annaba</option>
                    <option value="Batna">Batna</option>
                    <option value="Blida">Blida</option>
                    <option value="Setif">Setif</option>
                    <option value="Chlef">Chlef</option>
                    <option value="Djelfa">Djelfa</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupPhone1">Phone Number</label>
                  <input
                    type="tel"
                    id="signupPhone1"
                    name="phone1"
                    placeholder="0XXXXXXXXX"
                    value={signupData.phone1}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                  <p className="phone-help-text">Or your parent's number</p>
                  <p className={`validation-feedback ${phone1Validation.isValid ? 'valid' : 'invalid'}`}>
                    {phone1Validation.message}
                  </p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupPhone2">Second Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="signupPhone2"
                    name="phone2"
                    placeholder="0XXXXXXXXX"
                    value={signupData.phone2}
                    onChange={handleSignupChange}
                    disabled={isLoading}
                  />
                  <p className="phone-help-text">Or your parent's number</p>
                  <p className={`validation-feedback ${phone2Validation.isValid ? 'valid' : 'invalid'}`}>
                    {phone2Validation.message}
                  </p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupEmail">Email</label>
                  <input
                    type="email"
                    id="signupEmail"
                    name="email"
                    placeholder="your@email.com"
                    value={signupData.email}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                  <p className={`validation-feedback ${emailValidation.isValid ? 'valid' : 'invalid'}`}>
                    {emailValidation.message}
                  </p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupPassword">Password</label>
                  <input
                    type={showSignupPassword ? 'text' : 'password'}
                    id="signupPassword"
                    name="password"
                    placeholder="••••••••"
                    value={signupData.password}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                  <i 
                    className={`fas ${showSignupPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon`}
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    aria-label="Toggle password visibility"
                  ></i>
                  <p className={`validation-feedback ${passwordValidation.isValid ? 'valid' : 'invalid'}`}>
                    {passwordValidation.message}
                  </p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="signupConfirmPassword">Confirm Password</label>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="signupConfirmPassword"
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={signupData.confirmPassword}
                    onChange={handleSignupChange}
                    required
                    disabled={isLoading}
                  />
                  <i 
                    className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon`}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle confirm password visibility"
                  ></i>
                  <p className={`validation-feedback ${confirmPasswordValidation.isValid ? 'valid' : 'invalid'}`}>
                    {confirmPasswordValidation.message}
                  </p>
                </div>
                
                {/* MOVED INSIDE SCROLL CONTAINER */}
                <div className="form-actions">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Creating Account...
                      </>
                    ) : (
                      'Sign Up'
                    )}
                  </button>
                </div>
                
                <p 
                  className="toggle-form-link" 
                  tabIndex="0" 
                  role="button"
                  onClick={() => switchForm('login')}
                  onKeyPress={(e) => e.key === 'Enter' && switchForm('login')}
                  aria-label="Switch to login"
                >
                  Already have an account? <strong>Login</strong>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;